import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

// Prisma.Decimal, used in API, is an API-compatible subclass of Decimal
const customDecimalSchema = z.custom<Decimal>((val) => Decimal.isDecimal(val), {
  error: 'Invalid value',
});

export const decimal = customDecimalSchema
  // @NOTE(shawk): this is important to make testing easier
  .transform((value) => new Decimal(value));

export const decimalLike = z.union([
  customDecimalSchema,
  z.string().min(1),
  z.number(),
]);

export const decimalLikeToDecimal = decimalLike.transform((value, ctx) => {
  try {
    const decimal = new Decimal(value);

    if (decimal.isNaN()) {
      throw new Error('Invalid decimal value');
    }

    return decimal;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e: unknown) {
    ctx.issues.push({
      code: 'custom',
      message: 'Not a valid number',
      input: value,
    });

    return z.NEVER;
  }
});

export const decimalToNumber = decimal.transform((value) => value.toNumber());

/**
 * The `unknownToDecimal` schema exists as a workaround, manipulating the
 * inferred input type in order to avoid an issue with type inference.
 *
 * Specifically when working with React Hook Form and Decimals, there is a
 * problem where some of RHF's helpers greedily distribute over (possibly) large
 * unions tracing all paths to leaf properties in the form data (i.e. the
 * fields). These unions are massively inflated because those type helpers
 * iterate over all of the properties on the Decimal type (class) even those
 * that are not actually fields. In cases with complex nested forms this can
 * exceed TypeScript's complexity limits and cause errors.
 */
export const unknownToDecimal = z.unknown().pipe(decimalLikeToDecimal);

export function decimalGt(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.lte(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be greater than ${value}`,
        input: ctx.value,
      });
    }
  };
}

export function decimalLt(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.gte(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be less than ${value}`,
        input: ctx.value,
      });
    }
  };
}

export function decimalGte(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.lt(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be greater than or equal to ${value}`,
        input: ctx.value,
      });
    }
  };
}

export function decimalLte(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.gt(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be less than or equal to ${value}`,
        input: ctx.value,
      });
    }
  };
}

export function decimalScaleLte(scale: number) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.dp() > scale) {
      ctx.issues.push({
        code: 'custom',
        message: `Must have no more than ${scale} decimal places`,
        input: ctx.value,
      });
    }
  };
}

export function decimalIntegerDigits(decimal: Decimal): number {
  const integerPart = decimal.abs().trunc().toFixed(0).replace(/^0+/, '');

  return integerPart.length === 0 ? 0 : integerPart.length;
}

export function decimalIntegerDigitsLte(maxIntegerDigits: number) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    const integerDigits = decimalIntegerDigits(ctx.value);

    if (integerDigits > maxIntegerDigits) {
      ctx.issues.push({
        code: 'custom',
        message: `Must have no more than ${maxIntegerDigits} digits before the decimal point`,
        input: ctx.value,
      });
    }
  };
}
